import { supabase, TABLES } from '../../config/supabase';
import {
  AssetAllocation,
  AuthError,
  BenchmarkComparison,
  HoldingAnalysis,
  PerformanceAlert,
  PerformanceDataPoint,
  PortfolioPerformance,
  ReportData,
  SectorAllocation,
} from '../../types';
import { CACHE_TTL, EnhancedCache } from '../../utils/cache';
import { portfolioService } from '../portfolio';

// Export new goal-based analytics services
export { goalAnalyticsService } from './goalAnalyticsService';
export { performanceCalculationService } from './performanceCalculationService';
export { alertNotificationService } from './alertNotificationService';

// Export types from new services
export type {
  GoalAnalytics,
  GoalPerformanceData,
  GoalContributionData,
  GoalProjection,
} from './goalAnalyticsService';

export type {
  PerformanceMetrics,
  TimeSeriesReturn,
  AssetAllocationAnalysis,
  BenchmarkAnalysis,
  RiskMetrics,
} from './performanceCalculationService';

export type {
  UserAlert,
  AlertRule,
  MilestoneAlert,
  PerformanceAlert as NewPerformanceAlert,
  RebalanceAlert,
  SyncIssueAlert,
  AlertType,
  AlertSeverity,
} from './alertNotificationService';

// Realistic benchmark data based on historical market performance
// S&P 500 (SPY) and Bond Index (AGG) average returns by time period
const BENCHMARK_DATA: Record<string, Record<string, number>> = {
  SPY: {
    '1D': 0.08, // Average daily return
    '1W': 0.35, // Average weekly return
    '1M': 1.2, // Average monthly return
    '3M': 3.5, // Average quarterly return
    '6M': 7.2, // Average 6-month return
    '1Y': 10.8, // Average annual return (including dividends)
    ALL: 9.5, // Average long-term annual return
  },
  AGG: {
    '1D': 0.02, // Average daily return for bonds
    '1W': 0.08, // Average weekly return for bonds
    '1M': 0.3, // Average monthly return for bonds
    '3M': 0.9, // Average quarterly return for bonds
    '6M': 1.8, // Average 6-month return for bonds
    '1Y': 3.2, // Average annual return for bonds
    ALL: 4.1, // Average long-term annual return for bonds
  },
};

// Mock sector mapping - in production this would come from a financial data API
const SECTOR_MAPPING: Record<string, string> = {
  AAPL: 'Technology',
  GOOGL: 'Technology',
  MSFT: 'Technology',
  TSLA: 'Consumer Discretionary',
  SPY: 'Diversified',
  VTI: 'Diversified',
  QQQ: 'Technology',
};

export class AnalyticsService {
  private static instance: AnalyticsService;
  private cache: EnhancedCache;

  private constructor() {
    this.cache = new EnhancedCache({
      defaultTTL: CACHE_TTL.MEDIUM,
      maxSize: 50,
    });
  }

  static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  // ============================================================================
  // Portfolio Performance Analysis
  // ============================================================================

  /**
   * Calculates portfolio performance over time - uses real historical data from portfolio_history view
   * Requirements: 10.1, 10.2 - performance charts and visualization over time
   */
  async getPortfolioPerformance(
    profileId: string,
    timeRange: '1D' | '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL'
  ): Promise<{ performance?: PortfolioPerformance; error?: AuthError }> {
    try {
      // Get historical data from portfolio_history view
      const historicalData = await this.getHistoricalPortfolioData(
        profileId,
        timeRange
      );

      if (historicalData && historicalData.length > 0) {
        const startValue = historicalData[0].value;
        const endValue = historicalData[historicalData.length - 1].value;
        const totalReturn = endValue - startValue;
        const totalReturnPercent =
          startValue > 0 ? (totalReturn / startValue) * 100 : 0;

        const performance: PortfolioPerformance = {
          timeRange,
          data: historicalData,
          totalReturn,
          totalReturnPercent,
          startValue,
          endValue,
          startDate: historicalData[0].date,
          endDate: historicalData[historicalData.length - 1].date,
        };

        return { performance };
      }

      // No historical data - return empty performance
      return {
        performance: {
          timeRange,
          data: [],
          totalReturn: 0,
          totalReturnPercent: 0,
          startValue: 0,
          endValue: 0,
          startDate: new Date().toISOString(),
          endDate: new Date().toISOString(),
        },
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while calculating performance',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Gets historical portfolio data from portfolio_history view
   */
  private async getHistoricalPortfolioData(
    profileId: string,
    timeRange: '1D' | '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL'
  ): Promise<PerformanceDataPoint[]> {
    const cacheKey = `portfolio-history-${profileId}-${timeRange}`;
    const cached = this.cache.get(cacheKey) as PerformanceDataPoint[] | null;
    if (cached) {
      return cached;
    }

    try {
      // Calculate date range based on timeRange
      const endDate = new Date();
      let startDate = new Date();

      switch (timeRange) {
        case '1D':
          startDate.setDate(endDate.getDate() - 1);
          break;
        case '1W':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case '1M':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case '3M':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case '6M':
          startDate.setMonth(endDate.getMonth() - 6);
          break;
        case '1Y':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        case 'ALL':
          startDate.setFullYear(endDate.getFullYear() - 10); // Look back 10 years for ALL
          break;
      }

      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      // Query portfolio_history view for historical data
      const { data, error } = await supabase
        .from(TABLES.PORTFOLIO_HISTORY)
        .select(
          'snapshot_date, total_value, daily_change, daily_change_percentage'
        )
        .eq('user_id', profileId)
        .gte('snapshot_date', startDateStr)
        .lte('snapshot_date', endDateStr)
        .order('snapshot_date', { ascending: true });

      if (error) {
        console.error('Error fetching portfolio history:', error);
        return [];
      }

      if (!data || data.length === 0) {
        this.cache.set(cacheKey, [], CACHE_TTL.MEDIUM);
        return [];
      }

      // Convert to PerformanceDataPoint format using view's calculated values
      const performanceData: PerformanceDataPoint[] = data.map(snapshot => {
        const value = snapshot.total_value;
        const date = new Date(snapshot.snapshot_date).toISOString();
        const gainLoss = snapshot.daily_change || 0;
        const gainLossPercent = snapshot.daily_change_percentage || 0;

        return {
          date,
          value,
          gainLoss,
          gainLossPercent,
        };
      });

      this.cache.set(cacheKey, performanceData, CACHE_TTL.MEDIUM);
      return performanceData;
    } catch (error) {
      console.error('Error getting historical portfolio data:', error);
      return [];
    }
  }

  /**
   * Gets asset allocation breakdown - falls back to account allocation if no holdings exist
   * Requirements: 10.3 - asset allocation charts and sector breakdown
   */
  async getAssetAllocation(
    profileId: string
  ): Promise<{ allocation: AssetAllocation[]; error?: AuthError }> {
    try {
      const { analysis, error } =
        await portfolioService.getHoldingAnalysis(profileId);

      if (error) {
        return { allocation: [], error };
      }

      // If we have individual holdings, use them for asset allocation
      if (analysis.length > 0) {
        // Aggregate holdings by symbol to avoid duplicates
        const holdingsBySymbol = new Map<
          string,
          {
            totalValue: number;
            holdings: HoldingAnalysis[];
          }
        >();

        analysis.forEach(holding => {
          if (holding.currentValue && holding.currentValue > 0) {
            const symbol = holding.symbol;
            const existing = holdingsBySymbol.get(symbol) || {
              totalValue: 0,
              holdings: [],
            };
            existing.totalValue += holding.currentValue;
            existing.holdings.push(holding);
            holdingsBySymbol.set(symbol, existing);
          }
        });

        const totalValue = Array.from(holdingsBySymbol.values()).reduce(
          (sum, data) => sum + data.totalValue,
          0
        );

        if (totalValue === 0) {
          return { allocation: [] };
        }

        const allocation: AssetAllocation[] = Array.from(
          holdingsBySymbol.entries()
        )
          .map(([symbol, data]) => {
            // Use the first holding's security info for the aggregated entry
            const firstHolding = data.holdings[0];
            return {
              ticker: symbol,
              name: firstHolding.security?.name || symbol,
              value: data.totalValue,
              percentage: (data.totalValue / totalValue) * 100,
              sector: SECTOR_MAPPING[symbol] || 'Other',
              type: firstHolding.security?.type || 'stock',
            };
          })
          .sort((a, b) => b.value - a.value);

        return { allocation };
      }

      // If no individual holdings, fall back to account allocation
      return this.getAccountAllocation();
    } catch (error) {
      return {
        allocation: [],
        error: {
          code: 'UNKNOWN_ERROR',
          message:
            'An unexpected error occurred while calculating asset allocation',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Gets account allocation breakdown when no individual holdings exist
   */
  private async getAccountAllocation(): Promise<{
    allocation: AssetAllocation[];
    error?: AuthError;
  }> {
    try {
      // Import account service dynamically to avoid circular dependencies
      const { AccountService } = await import(
        '../../services/accounts/accountService'
      );

      const accountResult = await AccountService.getUserAccounts();
      if (!accountResult.success || !accountResult.data) {
        return { allocation: [] };
      }

      const accounts = accountResult.data.filter(
        account => account.currentBalance > 0
      );
      const totalValue = accounts.reduce(
        (sum, account) => sum + account.currentBalance,
        0
      );

      if (totalValue === 0) {
        return { allocation: [] };
      }

      const allocation: AssetAllocation[] = accounts.map(account => ({
        ticker: account.name,
        name: account.name,
        value: account.currentBalance,
        percentage: (account.currentBalance / totalValue) * 100,
        sector: account.accountType || 'Other',
        type: 'other' as const,
      }));

      return { allocation };
    } catch (error) {
      return {
        allocation: [],
        error: {
          code: 'UNKNOWN_ERROR',
          message:
            'An unexpected error occurred while calculating account allocation',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Gets sector allocation breakdown - falls back to account allocation if no holdings exist
   * Requirements: 10.3 - asset allocation charts and sector breakdown
   */
  async getSectorAllocation(
    profileId: string
  ): Promise<{ sectors: SectorAllocation[]; error?: AuthError }> {
    try {
      const { allocation, error } = await this.getAssetAllocation(profileId);

      if (error) {
        return { sectors: [], error };
      }

      const sectorMap = new Map<
        string,
        { value: number; holdings: string[] }
      >();
      const totalValue = allocation.reduce(
        (sum, asset) => sum + asset.value,
        0
      );

      allocation.forEach(asset => {
        const sector = asset.sector || 'Other';
        const existing = sectorMap.get(sector) || { value: 0, holdings: [] };
        existing.value += asset.value;
        existing.holdings.push(asset.ticker);
        sectorMap.set(sector, existing);
      });

      const sectors: SectorAllocation[] = Array.from(sectorMap.entries())
        .map(([sector, data]) => ({
          sector,
          value: data.value,
          percentage: totalValue > 0 ? (data.value / totalValue) * 100 : 0,
          holdings: data.holdings,
        }))
        .sort((a, b) => b.value - a.value);

      return { sectors };
    } catch (error) {
      return {
        sectors: [],
        error: {
          code: 'UNKNOWN_ERROR',
          message:
            'An unexpected error occurred while calculating sector allocation',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Gets individual holding performance analysis
   * Requirements: 10.4 - individual holding performance analysis
   */
  async getHoldingPerformanceAnalysis(profileId: string): Promise<{
    topPerformers: HoldingAnalysis[];
    bottomPerformers: HoldingAnalysis[];
    error?: AuthError;
  }> {
    try {
      const { analysis, error } =
        await portfolioService.getHoldingAnalysis(profileId);

      if (error) {
        return { topPerformers: [], bottomPerformers: [], error };
      }

      // Filter holdings with valid performance data
      const holdingsWithPerformance = analysis.filter(
        holding =>
          holding.gainLossPercent !== undefined &&
          holding.currentValue &&
          holding.currentValue > 0
      );

      // Sort by performance percentage
      const sortedByPerformance = [...holdingsWithPerformance].sort(
        (a, b) => (b.gainLossPercent || 0) - (a.gainLossPercent || 0)
      );

      const topPerformers = sortedByPerformance.slice(0, 5);
      const bottomPerformers = sortedByPerformance.slice(-5).reverse();

      return { topPerformers, bottomPerformers };
    } catch (error) {
      return {
        topPerformers: [],
        bottomPerformers: [],
        error: {
          code: 'UNKNOWN_ERROR',
          message:
            'An unexpected error occurred while analyzing holding performance',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Gets all holdings with performance data for comprehensive analysis
   */
  async getAllHoldingPerformance(profileId: string): Promise<{
    allHoldings: Array<{
      symbol: string;
      gainLossPercent?: number;
    }>;
    error?: AuthError;
  }> {
    try {
      const { analysis, error } =
        await portfolioService.getHoldingAnalysis(profileId);

      if (error) {
        return { allHoldings: [], error };
      }

      // Filter holdings with valid performance data
      const holdingsWithPerformance = analysis.filter(
        holding =>
          holding.gainLossPercent !== undefined &&
          holding.currentValue &&
          holding.currentValue > 0
      );

      const allHoldings = holdingsWithPerformance.map(holding => ({
        symbol: holding.symbol,
        gainLossPercent: holding.gainLossPercent,
      }));

      return { allHoldings };
    } catch (error) {
      return {
        allHoldings: [],
        error: {
          code: 'UNKNOWN_ERROR',
          message:
            'An unexpected error occurred while getting all holding performance data',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Gets daily performance data for all holdings (used by HoldingsTreemap)
   */
  async getHoldingDailyPerformance(profileId: string): Promise<{
    holdings: Array<{
      symbol: string;
      dailyChangePercent?: number;
    }>;
    error?: AuthError;
  }> {
    try {
      const { analysis, error } =
        await portfolioService.getHoldingAnalysis(profileId);

      if (error) {
        return { holdings: [], error };
      }

      // Filter holdings with valid daily performance data
      const holdingsWithDailyPerformance = analysis.filter(
        holding =>
          holding.dailyChangePercent !== undefined &&
          holding.currentValue &&
          holding.currentValue > 0
      );

      const holdings = holdingsWithDailyPerformance.map(holding => ({
        symbol: holding.symbol,
        dailyChangePercent: holding.dailyChangePercent,
      }));

      return { holdings };
    } catch (error) {
      return {
        holdings: [],
        error: {
          code: 'UNKNOWN_ERROR',
          message:
            'An unexpected error occurred while getting daily performance data',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Compares portfolio performance vs benchmark
   * Requirements: 10.6 - portfolio vs benchmark comparison features
   */
  async getBenchmarkComparison(
    profileId: string,
    timeRange: '1D' | '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL',
    benchmarkTicker: string = 'SPY'
  ): Promise<{ comparison?: BenchmarkComparison; error?: AuthError }> {
    try {
      const { performance, error } = await this.getPortfolioPerformance(
        profileId,
        timeRange
      );

      if (error || !performance) {
        return { error };
      }

      const benchmarkReturn = BENCHMARK_DATA[benchmarkTicker]?.[timeRange] || 0;
      const portfolioReturn = performance.totalReturnPercent;
      const outperformance = portfolioReturn - benchmarkReturn;

      const comparison: BenchmarkComparison = {
        portfolioReturn,
        benchmarkReturn,
        outperformance,
        benchmarkName:
          benchmarkTicker === 'SPY'
            ? 'S&P 500'
            : benchmarkTicker === 'AGG'
              ? 'Bond Index'
              : benchmarkTicker,
        timeRange,
      };

      return { comparison };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while comparing to benchmark',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Processes raw analytics data into chart-ready format
   * Requirements: 10.1, 10.2, 10.3 - chart data processing
   */
  async getProcessedAnalyticsData(
    profileId: string,
    timeRange: '1D' | '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL'
  ): Promise<{
    performance?: PortfolioPerformance;
    assetAllocation?: AssetAllocation[];
    sectorAllocation?: SectorAllocation[];
    holdingPerformance?: {
      topPerformers: HoldingAnalysis[];
      bottomPerformers: HoldingAnalysis[];
      allHoldings: Array<{
        symbol: string;
        gainLossPercent?: number;
      }>;
      dailyPerformance: Array<{
        symbol: string;
        dailyChangePercent?: number;
      }>;
    };
    benchmarkComparison?: BenchmarkComparison;
    error?: AuthError;
  }> {
    try {
      const [
        performanceResult,
        allocationResult,
        sectorResult,
        holdingResult,
        allHoldingResult,
        dailyPerformanceResult,
        benchmarkResult,
      ] = await Promise.all([
        this.getPortfolioPerformance(profileId, timeRange),
        this.getAssetAllocation(profileId),
        this.getSectorAllocation(profileId),
        this.getHoldingPerformanceAnalysis(profileId),
        this.getAllHoldingPerformance(profileId),
        this.getHoldingDailyPerformance(profileId),
        this.getBenchmarkComparison(profileId, timeRange),
      ]);

      if (performanceResult.error) {
        return { error: performanceResult.error };
      }

      return {
        performance: performanceResult.performance,
        assetAllocation: allocationResult.allocation || [],
        sectorAllocation: sectorResult.sectors || [],
        holdingPerformance: {
          topPerformers: holdingResult.topPerformers || [],
          bottomPerformers: holdingResult.bottomPerformers || [],
          allHoldings: allHoldingResult.allHoldings || [],
          dailyPerformance: dailyPerformanceResult.holdings || [],
        },
        benchmarkComparison: benchmarkResult.comparison,
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message:
            'An unexpected error occurred while processing analytics data',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Generates comprehensive report data
   * Requirements: 10.5 - exportable reports in PDF and CSV formats
   */
  async generateReportData(
    userId: string,
    timeRange: '1M' | '3M' | '6M' | '1Y' | 'ALL'
  ): Promise<{ reportData?: ReportData; error?: AuthError }> {
    try {
      // Get user name
      const { data: userData, error: userError } = await supabase
        .from(TABLES.USER_PROFILES)
        .select('first_name, last_name')
        .eq('id', userId)
        .single();

      if (userError) {
        return {
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch user data',
            details: userError as unknown as Record<string, unknown>,
          },
        };
      }

      const userName = userData
        ? `${userData.first_name || ''} ${userData.last_name || ''}`.trim() ||
          'User'
        : 'User';

      // Gather all required data
      const [
        { summary },
        { performance },
        { allocation },
        { sectors },
        { topPerformers, bottomPerformers },
        { comparison },
      ] = await Promise.all([
        portfolioService.calculatePortfolioSummary(userId),
        this.getPortfolioPerformance(userId, timeRange),
        this.getAssetAllocation(userId),
        this.getSectorAllocation(userId),
        this.getHoldingPerformanceAnalysis(userId),
        this.getBenchmarkComparison(userId, timeRange),
      ]);

      if (!summary || !performance) {
        return {
          error: {
            code: 'DATA_ERROR',
            message: 'Failed to gather required report data',
          },
        };
      }

      const reportData: ReportData = {
        userId,
        userName,
        reportDate: new Date().toISOString(),
        timeRange,
        portfolioSummary: summary,
        performance,
        assetAllocation: allocation || [],
        sectorAllocation: sectors || [],
        topPerformers: topPerformers || [],
        bottomPerformers: bottomPerformers || [],
        benchmarkComparison: comparison,
      };

      return { reportData };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while generating report data',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  // ============================================================================
  // Performance Alerts
  // ============================================================================

  /**
   * Creates a performance alert
   * Requirements: 10.6 - performance alerts and notifications
   */
  async createPerformanceAlert(
    userId: string,
    type:
      | 'gain_threshold'
      | 'loss_threshold'
      | 'portfolio_value'
      | 'holding_change',
    condition: 'above' | 'below' | 'equals',
    threshold: number
  ): Promise<{ alert?: PerformanceAlert; error?: AuthError }> {
    try {
      const alertRecord = {
        user_id: userId,
        type,
        condition,
        threshold,
        current_value: 0, // Will be updated when checked
        is_triggered: false,
        message: this.generateAlertMessage(type, condition, threshold),
        created_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from(TABLES.USER_ALERTS)
        .insert(alertRecord)
        .select()
        .single();

      if (error) {
        return {
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to create performance alert',
            details: error as unknown as Record<string, unknown>,
          },
        };
      }

      const alert: PerformanceAlert = {
        id: data.id,
        userId: data.user_id,
        type: data.type,
        condition: data.condition,
        threshold: data.threshold,
        currentValue: data.current_value,
        isTriggered: data.is_triggered,
        message: data.message,
        createdAt: data.created_at,
        triggeredAt: data.triggered_at,
      };

      return { alert };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while creating alert',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Gets all performance alerts for a profile
   * Requirements: 10.6 - performance alerts and notifications
   */
  async getPerformanceAlerts(
    userId: string
  ): Promise<{ alerts: PerformanceAlert[]; error?: AuthError }> {
    try {
      const { data, error } = await supabase
        .from(TABLES.USER_ALERTS)
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        return {
          alerts: [],
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch performance alerts',
            details: error as unknown as Record<string, unknown>,
          },
        };
      }

      const alerts: PerformanceAlert[] = (data || []).map(row => ({
        id: row.id,
        userId: row.user_id,
        type: row.type,
        condition: row.condition,
        threshold: row.threshold,
        currentValue: row.current_value,
        isTriggered: row.is_triggered,
        message: row.message,
        createdAt: row.created_at,
        triggeredAt: row.triggered_at,
      }));

      return { alerts };
    } catch (error) {
      return {
        alerts: [],
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while fetching alerts',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private generateAlertMessage(
    type: string,
    condition: string,
    threshold: number
  ): string {
    const formattedThreshold =
      type === 'portfolio_value'
        ? `$${threshold.toLocaleString()}`
        : `${threshold}%`;

    switch (type) {
      case 'gain_threshold':
        return `Alert when portfolio gains ${condition} ${formattedThreshold}`;
      case 'loss_threshold':
        return `Alert when portfolio losses ${condition} ${formattedThreshold}`;
      case 'portfolio_value':
        return `Alert when portfolio value ${condition} ${formattedThreshold}`;
      case 'holding_change':
        return `Alert when any holding changes ${condition} ${formattedThreshold}`;
      default:
        return `Performance alert: ${condition} ${formattedThreshold}`;
    }
  }
}

// Export singleton instance (legacy service for backward compatibility)
export const analyticsService = AnalyticsService.getInstance();

// Note: The legacy AnalyticsService above is maintained for backward compatibility.
// New implementations should use the goal-based analytics services:
// - goalAnalyticsService: For goal-specific analytics and dashboards
// - performanceCalculationService: For performance metrics and calculations
// - alertNotificationService: For alerts and notifications
// - historicalTrackingService: For historical data and trend analysis
